import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

const FEEDBACK_MS = 1000;
const COPY_ICON_SELECTOR = 'i.fa-copy, i.copyIcon, i.copyUrlIcon, i.copy-icon';
const DEFAULT_MESSAGE_KEY = 'admin.global.copied';
const FAILURE_MESSAGE_KEY = 'admin.global.copyFailed';

export interface ClipboardCopyOptions {
  /** Translation key shown in the inline tooltip. Defaults to `admin.global.copied`. */
  messageKey?: string;
  /** Whether a refused copy shows the failure toaster. Defaults to true. */
  toastOnFailure?: boolean;
  /** Extra class put on both the check icon and the tooltip, for theming. */
  themeClass?: string;
}

/**
 * Storefront flavour of the inline feedback (#11629): the customer UI loads its
 * own translation set, paints the check and the tooltip in the store theme
 * colour instead of the admin brand colour, and stays silent when the browser
 * refuses the copy rather than claiming a copy that did not happen.
 */
export const PUBLIC_COPY_OPTIONS: ClipboardCopyOptions = {
  // Renamed from `public.global.referenceCopied`, which was written for one
  // sentence — the incident reference on the error page — and is now the
  // storefront's generic label. Its English value was already the neutral
  // "Copied", but a translator working from the key name would reasonably
  // have written "Reference copied" and it would then have appeared on a
  // voucher code in My vouchers. The other language columns are still empty,
  // so renaming now costs nothing and removes the trap; the error page's own
  // inline "Copied" line went away with this story, leaving this the key's
  // only consumer.
  messageKey: 'public.global.copied',
  toastOnFailure: false,
  themeClass: 'clipboard-copied--store-theme',
};

@Injectable({ providedIn: 'root' })
export class ClipboardCopyService {
  private activeFeedbackTimer: number | null = null;
  private activeTooltip: HTMLElement | null = null;
  private activeIcon: HTMLElement | null = null;
  private activeIconRestore: (() => void) | null = null;

  constructor(
    private readonly toastr: ToastrService,
    private readonly translateService: TranslateService,
  ) {}

  /**
   * Copies text to the clipboard.
   * On success, shows a check icon + "Copied" tooltip (whitelabel colours) — no success toaster.
   * On failure, shows an error toaster unless `options.toastOnFailure` is false.
   */
  copy(
    text: string,
    trigger?: Event | HTMLElement | null,
    options?: ClipboardCopyOptions,
  ): void {
    const toastOnFailure = options?.toastOnFailure ?? true;
    if (!text) {
      this.notifyFailure(toastOnFailure);
      return;
    }
    const clipboardWrite = navigator.clipboard?.writeText(text);
    if (!clipboardWrite) {
      this.notifyFailure(toastOnFailure);
      return;
    }
    clipboardWrite
      .then(() => {
        this.showInlineCopiedFeedback(
          this.resolveTrigger(trigger),
          options?.messageKey ?? DEFAULT_MESSAGE_KEY,
          options?.themeClass,
        );
      })
      .catch(() => {
        this.notifyFailure(toastOnFailure);
      });
  }

  private notifyFailure(toastOnFailure: boolean): void {
    if (!toastOnFailure) {
      return;
    }
    this.toastr.error(this.translateService.instant(FAILURE_MESSAGE_KEY));
  }

  private resolveTrigger(
    trigger?: Event | HTMLElement | null,
  ): HTMLElement | null {
    if (!trigger) {
      return null;
    }
    if (trigger instanceof HTMLElement) {
      return trigger;
    }

    const target = trigger.target as HTMLElement | null;
    const currentTarget = trigger.currentTarget as HTMLElement | null;

    const iconFromTarget = target?.closest?.(
      COPY_ICON_SELECTOR,
    ) as HTMLElement | null;
    if (iconFromTarget) {
      return iconFromTarget;
    }

    if (currentTarget?.matches?.(COPY_ICON_SELECTOR)) {
      return currentTarget;
    }

    return currentTarget?.querySelector?.(
      COPY_ICON_SELECTOR,
    ) as HTMLElement | null;
  }

  private clearActiveFeedback(): void {
    if (this.activeFeedbackTimer != null) {
      window.clearTimeout(this.activeFeedbackTimer);
      this.activeFeedbackTimer = null;
    }
    this.activeTooltip?.remove();
    this.activeTooltip = null;
    if (this.activeIconRestore) {
      this.activeIconRestore();
      this.activeIconRestore = null;
    }
    this.activeIcon = null;
  }

  private showInlineCopiedFeedback(
    el: HTMLElement | null,
    messageKey: string,
    themeClass?: string,
  ): void {
    if (!el) {
      return;
    }

    this.clearActiveFeedback();

    const message = this.translateService.instant(messageKey);
    const previousTitle = el.getAttribute('title');
    const previousAriaLabel = el.getAttribute('aria-label');

    // Avoid native browser title tooltip overlapping the custom one
    el.removeAttribute('title');
    el.setAttribute('aria-label', message);
    el.classList.add('clipboard-copied-icon');
    if (themeClass) {
      el.classList.add(themeClass);
    }

    const tooltip = document.createElement('span');
    tooltip.className = themeClass
      ? `clipboard-copied-tooltip ${themeClass}`
      : 'clipboard-copied-tooltip';
    tooltip.setAttribute('role', 'status');
    tooltip.textContent = message;
    document.body.appendChild(tooltip);

    const positionTooltip = () => {
      const rect = el.getBoundingClientRect();
      // 80px approximates the default width of the "Copied" tooltip (font + padding)
      // before layout has completed synchronously; requestAnimationFrame below
      // re-measures and updates with the actual rendered width.
      const tooltipW = tooltip.getBoundingClientRect().width || 80;
      const margin = 8;
      const centeredLeft = rect.left + rect.width / 2;

      // Clamp within the nearest clipping ancestor (dialog, overlay pane, etc.)
      // so the tooltip never bleeds outside the dialog boundary.
      // Falls back to the full viewport when no dialog ancestor is found.
      const clipAncestor = el.closest(
        '.mat-mdc-dialog-surface, .cdk-overlay-pane, [role="dialog"]',
      ) as HTMLElement | null;
      const clip = clipAncestor
        ? clipAncestor.getBoundingClientRect()
        : { left: 0, right: window.innerWidth };

      const minLeft = Math.max(margin, clip.left + margin) + tooltipW / 2;
      const maxLeft =
        Math.min(window.innerWidth - margin, clip.right - margin) -
        tooltipW / 2;
      const clampedLeft =
        maxLeft < minLeft
          ? (minLeft + maxLeft) / 2
          : Math.min(maxLeft, Math.max(minLeft, centeredLeft));

      tooltip.style.left = `${clampedLeft}px`;
      tooltip.style.top = `${rect.top}px`;
    };
    positionTooltip();
    requestAnimationFrame(positionTooltip);

    this.activeTooltip = tooltip;
    this.activeIcon = el;
    this.activeIconRestore = () => {
      if (previousTitle != null) {
        el.setAttribute('title', previousTitle);
      } else {
        el.removeAttribute('title');
      }
      if (previousAriaLabel != null) {
        el.setAttribute('aria-label', previousAriaLabel);
      } else {
        el.removeAttribute('aria-label');
      }
      el.classList.remove('clipboard-copied-icon');
      if (themeClass) {
        el.classList.remove(themeClass);
      }
    };

    this.activeFeedbackTimer = window.setTimeout(() => {
      this.clearActiveFeedback();
    }, FEEDBACK_MS);
  }
}
